import _ from 'lodash';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, takeWhile } from 'rxjs/operators';
import { selectDialogState } from 'app/dialog/features/dialog.selectors';
import { DialogService } from 'app/dialog/services/dialog.service';
import { ImageService } from 'app/core/services/image.service';
import { ImageCollection } from '../../models/images.collection';
import { LoadingService } from 'app/core/services/loading.service';
import { ImportImageService } from '../../services/import-images.service';
import { SlideItemSchema } from 'app/campaign/structure/slide-items';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-import-slide',
  templateUrl: './import-slide.component.html',
  styleUrls: ['./import-slide.component.scss']
})
export class ImportSlideComponent implements OnInit, OnDestroy {
  destroyRef = inject(DestroyRef)
  public dialog$: Observable<any> = this.store.pipe(select(selectDialogState));

  public step: number = 1;

  public schema: any = SlideItemSchema;
  public files: string[] = ["/files/sample_slide_item_import.xlsx"];

  public import: FormControl = new FormControl(null, []);
  public options: FormArray = new FormArray([], []);

  public hasImages: boolean = false;
  public images: ImageCollection = new ImageCollection();
  public imageUrls: number = 0;
  public localImages: number = 0;

  public choosenImages: any[] = [];
  public missedImages: any[] = [];

  public hasLinks: boolean = false;

  public upload: boolean = false;
  public confirm: boolean = false;
  public closeButton: boolean = false;

  public readmore: boolean = false;

  public isNotFirstStep: boolean = false;
  public isNotLastStep: boolean = true;

  public isUploading: boolean = false;
  public prevStepDisabled: boolean = false;
  public nextStepDisabled: boolean = true;
  public nextStepDisplay: boolean = true;

  constructor(
    private store: Store,
    private dialogService: DialogService,
    private imageService: ImageService,
    private importImageService: ImportImageService,
    private loadingService: LoadingService,
  ) {}

  ngOnInit(): void {
    this.import.valueChanges.pipe(
      filter(value => _.isNull(value)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.nextStepDisabled = true;
    });
  }

  ngOnDestroy(): void {
    this.dialogService.closeDialogSubject.next(null);
  }

  onChangeOptions(options): void {
    this.options = options;

    if(this.options.length < 1) {
      return;
    }

    const images = this.importImageService.getImages(this.options);
    this.imageUrls = this.importImageService.getImageUrls(images);
    this.localImages = this.importImageService.getLocalImages(images);

    this.hasImages = !!_.find(this.options.controls, option => {
      const imageUrl: FormControl = option.get('vImageUrl');
      const imageUrlValue = imageUrl.value;
      return _.isEqual(imageUrlValue, "/images/blank_2x1.png") ? false: true;
    });

    this.hasLinks = !!_.find(this.options.controls, option => {
      const imageUrl: FormControl = option.get('vImageUrl');

      let imageUrlValue = null;
      try {
        imageUrlValue = new URL(imageUrl.value);
      } catch(e) {}

      return _.isNull(imageUrlValue) ? false: true;
    });

    if(this.hasImages || this.hasLinks) {
      this.nextStepDisabled = false;
    } else {
      this.nextStepDisplay = false;
      this.confirm = true;
    }
  }

  onChangeImages(images): void {
    this.images.reset();
    this.images.addImageList(images);

    if(this.images.length > 0 || this.hasLinks) {
      let found = false;
      _.forEach(this.images.list, ({ error }) => {
        if(error) { found = true }
      })
      this.nextStepDisabled = false;
    } else {
      this.nextStepDisabled = true;
    }
    this.prevStepDisabled = false;

    this.upload = true;
    this.confirm = false;
  }

  onChangeFiles(files): void {
    this.choosenImages = files;
  }

  onCgangeMissed(missed): void {
    this.missedImages = missed;
  }

  onStart(dialog) {
    const { images } = this;

    this.isUploading = true;
    this.prevStepDisabled = true;
    this.nextStepDisabled = true;

    images.status = 1;

    this.loadingService.enabled = false;
    of.apply(null, this.images.list).pipe(
      concatMap((image: any, index: number) => {
        images.index = index;

        if(_.has(image, 'url')) {
          return of(image).pipe(
            concatMap(image => {
              return this.imageService.downloadImageBlob(image.url).then(blob => {
                return { ...image, blob };
              })
            }),
            concatMap((image: any) => {
              const segments = image.url.split("/");
              const name = segments.length > 0 ? segments[segments.length-1]: "";
              return this.imageService.uploadImageByBlobAndCrop(image.blob, name).pipe(
                map((result: any) => {
                  return { ...result, name: image.url };
                })
              );
            }),
            catchError((error: any) => {
              const status = _.get(error, 'status', null);
              if(status === 413) {
                return of({
                  error: "File size cannot be more than 5MB",
                })
              }
              return of({
                error: "Could not upload image",
              });
            })
          )
        }
        const file = image.file;
        return this.imageService.uploadImageByFileAndCrop(file.name, file).pipe(
          concatMap(result => {
            const fileName = _.split(file.name, '.');
            return of({ ...result, name: fileName[0] });
          }),
          catchError(({ status }: any) => {
            if(status === 413) {
              return of({
                error: "File size cannot be more than 5MB",
              })
            }
            return of({
              error: "Could not upload image",
            });
          })
        )
      }),
      takeUntilDestroyed(this.destroyRef),
      takeWhile(() => {
        return images.status === 1;
      })
    ).subscribe(result => {
      const { index } = images;
      const image = images.list[index];

      if(_.has(result, 'error') == false) {
        images.index = index;

        const foundImageUrlList = _.filter(this.options.controls, option => {
          const name = _.get(image, 'file.name', null);
          const url = _.get(image, 'url', null);
          const imageUrl = option.get('vImageUrl') as FormControl;
          return _.isEqual(imageUrl.value, name) || _.isEqual(imageUrl.value, url);
        }) || [];
        if(foundImageUrlList.length > 0) {
          _.forEach(foundImageUrlList, foundImageUrl => {
            const imageUrl = foundImageUrl.get('imageUrl') as FormControl;
            imageUrl.setValue(result.imageUrl);
          });
        }

        image.imageUrl = result.imageUrl;
        image.uploaded = true;
        images.uploaded++;
      } else {
        image.error = result.error;
      }

      images.remaining--;
      if(images.length -1 === index) {
        images.status = 2;

        this.loadingService.enabled = true;
        this.isUploading = false;

        this.upload = false;
        this.confirm = true;

        this.prevStepDisabled = false;
      }
    });
  }

  onSkip(dialog) {
    this.upload = false;
    this.onConfirm(dialog);
  }

  onConfirm(dialog) {
    this.step++;
    this.confirm = false;
    this.closeButton = true;
  }

  onClose(dialog) {
    this.close(dialog, true);
  }

  onFinish() {
    this.upload = true;
  }

  goToPrevStep() {
    this.step--;

    this.isNotFirstStep = this.step !== 1;
    this.isNotLastStep = this.step !== 2;

    this.nextStepDisabled = false;
    this.nextStepDisplay = true;
    this.upload = false;
    this.confirm = false;

    this.images.reset();
  }

  goToNextStep() {
    this.step++;

    this.isNotFirstStep = this.step !== 1;
    this.isNotLastStep = this.step !== 2;

    switch(this.step) {
      case 2:
        if(this.hasLinks) {
          this.nextStepDisabled = false;
        }
        break;
      default:
        this.nextStepDisabled = true;
    }
    this.nextStepDisplay = false;
    this.upload = true;
  }

  close(dialog, value) {
    this.dialogService.closeDialogSubject.next(value);

    const { initialState: { callback } } = dialog;
    if(value) {
      callback(this.options);
    }
  }

  onToggleReadMore() {
    this.readmore = !this.readmore;
  }
}
